const HOUR_MS = 60 * 60 * 1000;
const LONG_RUNNING_MIN_DURATION_MS = 2 * HOUR_MS;
/** How long after its start a long-running entry can still be the Now target */
const LONG_RUNNING_TARGETABLE_MS = HOUR_MS;

/**
 * Whether a session is a long, staff-run block (e.g. an all-day activity) that sits early in the
 * schedule by start time but shouldn't keep being treated as what's happening "now"
 *
 * @param {Pick<import('./EventSession.js').EventSession, 'presenters' | 'getDurationInMilliseconds'>} session
 * @param {boolean} isLastOfDay Whether it's the final session listed for its day
 * @returns {boolean}
 */
export const isLongRunningStaffSession = (session, isLastOfDay) =>
	!isLastOfDay &&
	session.presenters.length === 1 &&
	session.presenters[0].trim().toLowerCase() === 'staff' &&
	session.getDurationInMilliseconds() > LONG_RUNNING_MIN_DURATION_MS;

/**
 * @typedef {Object} ScheduleEntryTimes
 * @prop {number} start Start time in epoch milliseconds
 * @prop {number} end End time in epoch milliseconds
 * @prop {boolean} [isLongRunning] Only targetable for the first hour after it starts
 */

/**
 * Find the entry that is in progress at {@link now}, or else the next one to start
 *
 * @template {ScheduleEntryTimes} T
 * @param {T[]} entries Entries in schedule order
 * @param {number} now Epoch milliseconds
 * @returns {T | undefined} `undefined` when {@link now} is before the first entry starts or after
 *   the last one ends
 */
export const findNowEntry = (entries, now) => {
	if (entries.length === 0) {
		return undefined;
	}
	const firstStart = Math.min(...entries.map(entry => entry.start));
	const lastEnd = Math.max(...entries.map(entry => entry.end));
	if (now < firstStart || now >= lastEnd) {
		return undefined;
	}

	const candidates = entries.filter(
		entry => !entry.isLongRunning || now < entry.start + LONG_RUNNING_TARGETABLE_MS
	);

	const inProgress = candidates.find(entry => entry.start <= now && now < entry.end);
	if (inProgress) {
		return inProgress;
	}

	return candidates.reduce(
		(next, entry) => (entry.start > now && (!next || entry.start < next.start) ? entry : next),
		undefined
	);
};

/**
 * Read the rendered schedule entries, skipping any hidden by the bookmark/search filters since
 * they have no layout to scroll to
 *
 * @returns {(ScheduleEntryTimes & { el: HTMLElement })[]}
 */
export const getRenderedScheduleEntries = () =>
	Array.from(document.querySelectorAll('.schedule-item'))
		.filter(el => el.offsetParent !== null)
		.map(el => ({
			el,
			start: Date.parse(el.querySelector('.js-startTime')?.dateTime),
			end: Date.parse(el.querySelector('.js-endTime')?.dateTime),
			isLongRunning: el.dataset.longRunning === 'true',
		}))
		.filter(entry => !Number.isNaN(entry.start) && !Number.isNaN(entry.end));
